/**
 * @flow
 */
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Field } from 'formik';
import debounce from 'lodash/debounce';
import React from 'react';
import { I18n } from 'utils/i18n';
import focusToEnd from 'utils/focusToEnd';
import keyIs, { BACKSPACE, DOWN, ENTER, UP } from 'utils/keyIs';
import useFocusRef from 'utils/useFocusRef';

type Props = {
  form: {
    getFieldMeta: Function,
  },
  remove: Function,
  insert: Function,
  name: string,
};

const OptionLabelInput = styled('input')(({ theme }) => ({
  color: theme.palette.text.primary,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  width: '100%',
  padding: 0,
  lineHeight: '22px',
}));

const QuestionOptions = ({ form: { getFieldMeta }, name, remove, insert }: Props) => {
  const { value } = getFieldMeta(name);
  const focusRef = useFocusRef();

  const addOption = debounce(
    (index = value.length) => insert(index + 1, { _id: Math.random().toString(), label: '' }),
    200,
    {
      leading: true,
      trailing: false,
    },
  );

  const nextOnEnter = e => {
    const index = +e.target.dataset.index;
    if (e.target.value !== '' && index === value.length - 1) {
      addOption();
    }
    if (keyIs(e, ENTER) && index < value.length - 1) {
      const input = e.target.parentNode.nextSibling.querySelector('input');
      if (input && input.value !== '') {
        addOption(index);
      } else {
        focusToEnd(input);
      }
    } else if (keyIs(e, DOWN) && index < value.length - 1) {
      const input = e.target.parentNode.nextSibling.querySelector('input');
      focusToEnd(input);
    } else if (keyIs(e, UP)) {
      const input =
        index > 0
          ? e.target.parentNode.previousSibling.querySelector('input')
          : e.target.closest('div').querySelector('input');
      focusToEnd(input);
    }
  };

  const onKeyDown = e => {
    const index = +e.target.dataset.index;
    if (keyIs(e, UP, DOWN, ENTER)) {
      e.preventDefault();
    } else if (keyIs(e, BACKSPACE) && e.target.value === '' && index < value.length - 1) {
      e.preventDefault();
      const input =
        index > 0
          ? e.target.parentNode.previousSibling.querySelector('input')
          : e.target.parentNode.nextSibling.querySelector('input');
      focusToEnd(input);
      remove(index);
    }
  };

  return (
    <Box
      component="ul"
      sx={{
        listStyleType: 'none',
        p: 0,
        pl: '10px',
        m: 0,
      }}
    >
      {value.map((option, index) => {
        return (
          <Box
            component="li"
            key={option._id}
            sx={{
              display: 'list-item',
              position: 'relative',
              '&:before': {
                content: `'-'`,
                position: 'absolute',
                marginLeft: '-10px',
              },
            }}
          >
            <Field name={`${name}.${index}.label`}>
              {({ field }) => (
                <OptionLabelInput
                  {...field}
                  placeholder={I18n.t('FormDrawer.QuestionField.Option.placeholder')}
                  onKeyUp={nextOnEnter}
                  onKeyDown={onKeyDown}
                  ref={index < value.length - 1 ? focusRef : undefined}
                  data-index={index}
                />
              )}
            </Field>
          </Box>
        );
      })}
    </Box>
  );
};

export default QuestionOptions;
